import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TabsContent } from "@/components/ui/tabs";
import React, { useState } from "react";
import {
  FieldArrayWithId,
  UseFieldArrayReturn,
  UseFormReturn,
} from "react-hook-form";
import { UserFormValues } from "./form";

interface UserModulesTabProps {
  form: UseFormReturn<UserFormValues>;
  fields: UseFieldArrayReturn<UserFormValues, "modules">["fields"];
}

interface Module {
  module_id: number;
  module_title: string;
  permission: number;
  parent_id?: number | null;
  children?: Module[];
  original_index?: number;
}

// Organize modules to group children with their parents
const organizeModules = (modules: Module[]) => {
  const moduleMap: { [key: number]: Module } = {};

  // First pass: create module objects
  modules.forEach((module) => {
    moduleMap[module.module_id] = {
      ...module,
      children: [],
    };
  });

  const rootModules: Module[] = [];

  // Second pass: organize hierarchy
  modules.forEach((module) => {
    if (module.parent_id && moduleMap[module.parent_id]) {
      moduleMap[module.parent_id].children?.push(moduleMap[module.module_id]);
    } else {
      rootModules.push(moduleMap[module.module_id]);
    }
  });

  return rootModules;
};

const updateChildPermissions = (
  module: Module,
  permissionBit: number,
  checked: boolean,
  form: UseFormReturn<UserFormValues>,
  fields: FieldArrayWithId<UserFormValues, "modules", "id">[],
) => {
  module.children?.forEach((child) => {
    const childIndex = fields.findIndex((f) => f.module_id === child.module_id);
    if (childIndex !== -1) {
      const currentPermission =
        form.getValues(`modules.${childIndex}.permission`) ?? 0;
      const newPermission = checked
        ? currentPermission | permissionBit
        : currentPermission & ~permissionBit;
      form.setValue(`modules.${childIndex}.permission`, newPermission);
      updateChildPermissions(child, permissionBit, checked, form, fields);
    }
  });
};

const UserModulesTab = ({ form, fields }: UserModulesTabProps) => {
  const [selectAll, setSelectAll] = useState(false);
  const organizedModules = organizeModules(fields);

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    const newValue = checked ? 30 : 0;
    fields.forEach((__, index) => {
      form.setValue(`modules.${index}.permission`, newValue);
    });
  };

  const renderModuleRow = (module: Module, level: number = 0) => {
    const moduleIndex = fields.findIndex(
      (f) => f.module_id === module.module_id,
    );

    return (
      <React.Fragment key={module.module_id}>
        <TableRow className={`${level > 0 ? "" : "bg-muted/95"}`}>
          <TableCell className={`${level > 0 ? "pl-8" : "font-bold"}`}>
            {module.module_title}
          </TableCell>
          <TableCell>
            <FormField
              control={form.control}
              name={`modules.${moduleIndex}.permission`}
              render={({ field: { value = 0, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Switch
                      className="bg-foreground"
                      checked={!!(value & 2)}
                      onCheckedChange={(checked) => {
                        onChange(checked ? value | 2 : value & ~2);
                        updateChildPermissions(
                          module,
                          2,
                          checked,
                          form,
                          fields,
                        );
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TableCell>
          <TableCell>
            <FormField
              control={form.control}
              name={`modules.${moduleIndex}.permission`}
              render={({ field: { value = 0, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Switch
                      className="bg-foreground"
                      checked={!!(value & 4)}
                      onCheckedChange={(checked) => {
                        onChange(checked ? value | 4 : value & ~4);
                        updateChildPermissions(
                          module,
                          4,
                          checked,
                          form,
                          fields,
                        );
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TableCell>
          <TableCell>
            <FormField
              control={form.control}
              name={`modules.${moduleIndex}.permission`}
              render={({ field: { value = 0, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Switch
                      className="bg-foreground"
                      checked={!!(value & 8)}
                      onCheckedChange={(checked) => {
                        onChange(checked ? value | 8 : value & ~8);
                        updateChildPermissions(
                          module,
                          8,
                          checked,
                          form,
                          fields,
                        );
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TableCell>
          <TableCell>
            <FormField
              control={form.control}
              name={`modules.${moduleIndex}.permission`}
              render={({ field: { value = 0, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Switch
                      className="bg-foreground"
                      checked={!!(value & 16)}
                      onCheckedChange={(checked) => {
                        onChange(checked ? value | 16 : value & ~16);
                        updateChildPermissions(
                          module,
                          16,
                          checked,
                          form,
                          fields,
                        );
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TableCell>
        </TableRow>
        {module.children?.map((childModule) =>
          renderModuleRow(childModule, level + 1),
        )}
      </React.Fragment>
    );
  };

  return (
    <TabsContent value="modules" className="space-y-6 mt-4">
      <div className="space-y-4">
        <div className="p-4 rounded-md">
          <label htmlFor="">Permitir tudo</label>
          <Switch
            id="select-all"
            className="bg-foreground ml-2"
            checked={selectAll}
            onCheckedChange={handleSelectAll}
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Módulo</TableHead>
                <TableHead>Visualizar</TableHead>
                <TableHead>Cadastrar</TableHead>
                <TableHead>Alterar</TableHead>
                <TableHead>Excluir</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizedModules.map((module) => renderModuleRow(module))}
            </TableBody>
          </Table>
        </div>
      </div>
    </TabsContent>
  );
};

export default UserModulesTab;
